import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import { NewAnomalyItem, FVIStructureItem, PDFImageProps, StructureInspectionTypeEntry, ConcreteHammerData, StructureUTMData } from '../../../../utils/models';
import { matchPhrases } from '../../../../utils/utilities';
import { getStructureAnomalyClassOptions, getStructureInspectionTypeOptions, getStructureNDESubTypeOptions, getUTMStructureInspectionOptions } from '../../../../utils/data';

import AccordionItem from '../accordion-item';
import ConcreteHammerTemplate from '../../custom-pcnl/concrete-hammer-template/report-form';
import Conditional from '../../../common/conditional';
import Confirm from '../../../themed/confirm';
import CustomAnomalyItems from '../../custom-general/custom-anomaly-items';
import CustomNewItem from '../../custom-general/custom-new-item';
import CustomUTMSection from '../../custom-utm/custom-utm-section';
import StructuralMemberForm from '../../../forms/custom-fvi/structuralMemberForm';
import StructureInspectionTypeForm from '../../../forms/custom-fvi/structureInspectionTypeForm';
import VIGeneralDrawingForm from '../../../forms/visual-inspection/viGeneralDrawingForm';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';

import classNames from './style.module.scss';

const INITIAL_HAMMER_TEST_DATA: ConcreteHammerData = {
	overview: {
		testDate: new Date(),
		surfaceCondition: 'As cleaned & Rough',
		stageOfTest: 'Aged Concreate',
		index: 0,
		hammerOrientation: 'Vertical',
		equipment: 'Dutrux 122D',
		serialNumber: '64947917',
		calibrationDueDate: new Date(),
		angle: 90,
		face: 'Bottom',
		refStd: 'ASTM C42/C42M-20; C1604/C1604M',
		acceptanceStd: 'BS 1881 - Part 202',
		material: 'Carbon steel',
		summary: '',
	},
	reading: {
		readingEndRange: 0,
		readingStartRange: 0,
		items: [],
	},
	locations: [],
};

const INITIAL_STRUCTURE_UTM_DATA: StructureUTMData = {
	overview: {
		ndeTechnique: '',
		equipment: '',
		probe: '',
		amplitudeLinearityCheck: '',
		screenHeightLinearityCheck: '',
		calibrationBlk: '',
		refStd: '',
		serialNo: '',
		scanningLevel: '',
		couplant: '',
		remarks: '',
		material: '',
		calibrationDueDate: new Date(),
		dateOfUTM: null,
		corrosionAllowance: 0,
		unit: 'mm',
		summary: '',
		techniqueType: '',
		velocity: 0,
	},
	tmlLocations: [],
	items: [],
};

interface ExistingStructureItemProps {
	nextAnomalyID?: number;
	structureItem: FVIStructureItem;
	isOpenOnDefault?: boolean;
	isPDOFormat: boolean;
	enableComponentInspectionDetails?: boolean;
	onDeleteStructureItem: (structureMemberName: string) => void;
	onChangeStructureAnomaly: (structureMemberName: string, items: NewAnomalyItem[]) => void;
	onChangeGeneralPhotos: (structureMemberName: string, images: PDFImageProps[]) => void;
	onChangeGeneralDrawing: (structureMemberName: string, images: PDFImageProps[]) => void;
	onChangeInspectionTypeEntries?: (structureMemberName: string, entries: StructureInspectionTypeEntry[]) => void;
	onChangeHammerTestData?: (structureMemberName: string, hammerTestData: ConcreteHammerData) => void;
	onChangeUTMData?: (structureMemberName: string, utmData: StructureUTMData) => void;
	onEditStructureItem: (initialItem: FVIStructureItem, updatedItem: Pick<FVIStructureItem,  'structureMemberName' | 'hasAnomalies' | 'inspectionDescription' | 'location'>) => void;
}

const ExistingStructureItem: React.FC<ExistingStructureItemProps> = ({
	structureItem,
	nextAnomalyID,
	isOpenOnDefault,
	isPDOFormat,
	enableComponentInspectionDetails = false,
	onDeleteStructureItem,
	onEditStructureItem,
	onChangeStructureAnomaly,
	onChangeGeneralPhotos,
	onChangeGeneralDrawing,
	onChangeInspectionTypeEntries = () => {},
	onChangeHammerTestData = () => {},
	onChangeUTMData = () => {},
}) => {
	const [deleteStructureItem, setDeleteStructureItem] = useState(false);
	const [editStructureItem, setEditStructureItem] = useState(false);
	const [isOpen, setOpen] = useState(false);

	const inspectionTypeEntries = structureItem.inspectionTypeEntries ?? [];
	const hasHammerTestEntry = inspectionTypeEntries.some((entry) => matchPhrases(entry.ndeSubType ?? '', 'Hammer Test'));
	const hasUTMEntry = inspectionTypeEntries.some((entry) => matchPhrases(entry.ndeSubType ?? '', 'Ultrasonic Thickness (UT)'));

	const handleEditInspectedItem = (newStructureDetails: Pick<FVIStructureItem, 'structureMemberName' | 'hasAnomalies' | 'inspectionDescription' | 'location'>) => {
		onEditStructureItem(structureItem, newStructureDetails);
		setEditStructureItem(false);
		setOpen(true);
	};

	const handleAddInspectionTypeEntry = (entry: Omit<StructureInspectionTypeEntry, 'id'>) => {
		const newEntry: StructureInspectionTypeEntry = { id: String(inspectionTypeEntries.length + 1), ...entry };
		onChangeInspectionTypeEntries(structureItem.structureMemberName, [...inspectionTypeEntries, newEntry]);
	};

	const handleDeleteInspectionTypeEntry = (id: string) => {
		onChangeInspectionTypeEntries(structureItem.structureMemberName, inspectionTypeEntries.filter((entry) => entry.id !== id));
	};

	const getInspectionTypeLabel = (entry: StructureInspectionTypeEntry) => {
		const typeLabel = getStructureInspectionTypeOptions().find((option) => option.id === entry.type)?.name ?? entry.type;
		if (!entry.ndeSubType) return typeLabel;

		const subTypeLabel = getStructureNDESubTypeOptions().find((option) => option.id === entry.ndeSubType)?.name ?? entry.ndeSubType;
		return `${typeLabel} - ${subTypeLabel}`;
	};

	const handleToggleEdit = () => {
		setEditStructureItem((previous) => !previous);
	};

	const handleConfirmDeletion = () => {
		onDeleteStructureItem(structureItem.structureMemberName);
	};

	const handleToggleDeletion = () => {
		setDeleteStructureItem((previous) => !previous);
	};

	useEffect(() => {
		if (isOpenOnDefault) setOpen(isOpenOnDefault);
	}, [isOpenOnDefault]);

	return (
		<>
			<Conditional isVisible={deleteStructureItem}>
				<Confirm
					title={'Delete Structural Member ' + structureItem.structureMemberName + '?'}
					message="Are you sure you want to delete this item? This action is not reversible!"
					onCancel={handleToggleDeletion}
					onConfirm={handleConfirmDeletion}
				/>
			</Conditional>

			<Conditional isVisible={editStructureItem}>
				<div className="bg-white round border p-3">
					<StructuralMemberForm
						structureDetails={{ ..._.pick(structureItem, ['structureMemberName', 'hasAnomalies', 'inspectionDescription']), location: structureItem.location ?? '' }}
						enableComponentInspectionDetails={enableComponentInspectionDetails}
						onSave={handleEditInspectedItem}
						onCancelEdit={handleToggleEdit}
					/>
				</div>
			</Conditional>

			<Conditional isVisible={!deleteStructureItem && !editStructureItem}>
				<div className={classNames.accordionItem}>
					<div className={classNames.accordionItemHeader} onClick={() => setOpen((prev) => !prev)} role="button">
						<div>
							Structure Member Name: {structureItem.structureMemberName}

							<Conditional isVisible={enableComponentInspectionDetails && !!structureItem.location}>
								<span> ({structureItem.location})</span>
							</Conditional>
						</div>

						<div className="d-flex justify-content-center align-items-center">
							<button className={classNames.accordionItemEdit} onClick={handleToggleEdit}>
								<i className="bi bi-pencil"></i>
							</button>

							<button className={classNames.accordionItemDelete} onClick={handleToggleDeletion}>
								<i className="bi bi-trash"></i>
							</button>

							<div className={classNames.accordionItemIcon}>
								<i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`}></i>
							</div>
						</div>
					</div>

					<Conditional isVisible={isOpen}>
						<div className={classNames.accordionItemBody}>
							<Conditional isVisible={matchPhrases(structureItem.hasAnomalies, 'yes')}>
								<AccordionItem isActive title="Anomaly Items">
									<CustomAnomalyItems
										isPDOFormat={isPDOFormat}
										nextAnomalyID={nextAnomalyID ?? structureItem.anomalyItems.length + 1}
										locationOptions={[]}
										isNewFormat={false}
										damageClasses={getStructureAnomalyClassOptions()}
										items={structureItem.anomalyItems}
										onDataChange={(items) => onChangeStructureAnomaly(structureItem.structureMemberName, items)}
									/>
								</AccordionItem>
							</Conditional>

							<Conditional isVisible={matchPhrases(structureItem.hasAnomalies, 'no')}>
								<AccordionItem isActive title="General Drawing">
									<VIGeneralDrawingForm 
										initialGeneralDrawing={structureItem.generalDrawing}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralDrawing(structureItem.structureMemberName, images)} 
									/>
								</AccordionItem>
								
								<AccordionItem isActive title="General Photos">
									<VIPhotoRecordForm 
										initialGeneralPhotos={structureItem.generalPhotos}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralPhotos(structureItem.structureMemberName, images)} 
									/>
								</AccordionItem>
							</Conditional>

							<Conditional isVisible={enableComponentInspectionDetails}>
								<AccordionItem isActive title="Inspection Types">
									<CustomNewItem label="Add Inspection Type">
										<StructureInspectionTypeForm onSave={handleAddInspectionTypeEntry} />
									</CustomNewItem>

									{inspectionTypeEntries.map((entry) => (
										<div key={entry.id} className={classNames.accordionItem}>
											<div className={classNames.accordionItemHeader}>
												<div>{getInspectionTypeLabel(entry)}</div>

												<button className={classNames.accordionItemDelete} onClick={() => handleDeleteInspectionTypeEntry(entry.id)}>
													<i className="bi bi-trash"></i>
												</button>
											</div>

											<div className={classNames.accordionItemBody}>
												{entry.description}
											</div>
										</div>
									))}
								</AccordionItem>

								<Conditional isVisible={hasHammerTestEntry}>
									<AccordionItem isActive title="Concrete Hammer Test Report">
										<ConcreteHammerTemplate
											item={structureItem.hammerTestData ?? INITIAL_HAMMER_TEST_DATA}
											onDataChange={(hammerTestData) => onChangeHammerTestData(structureItem.structureMemberName, hammerTestData)}
										/>
									</AccordionItem>
								</Conditional>

								<Conditional isVisible={hasUTMEntry}>
									<AccordionItem isActive title="UTM Data">
										<CustomUTMSection
											options={getUTMStructureInspectionOptions()}
											tmlLocations={(structureItem.utmData ?? INITIAL_STRUCTURE_UTM_DATA).tmlLocations}
											onChangeTMLLocations={(tmlLocations) => onChangeUTMData(structureItem.structureMemberName, { ...(structureItem.utmData ?? INITIAL_STRUCTURE_UTM_DATA), tmlLocations })}
											utmItems={(structureItem.utmData ?? INITIAL_STRUCTURE_UTM_DATA).items}
											utmOverview={(structureItem.utmData ?? INITIAL_STRUCTURE_UTM_DATA).overview}
											onItemsChange={(items) => onChangeUTMData(structureItem.structureMemberName, { ...(structureItem.utmData ?? INITIAL_STRUCTURE_UTM_DATA), items })}
											onOverviewChange={(overview) => onChangeUTMData(structureItem.structureMemberName, { ...(structureItem.utmData ?? INITIAL_STRUCTURE_UTM_DATA), overview })}
										/>
									</AccordionItem>
								</Conditional>
							</Conditional>
						</div>
					</Conditional>
				</div>
			</Conditional>
		</>
	);
};

export default ExistingStructureItem;
